export interface WeekDaySearch {
  week: number | null;
  day: number | null;
  text: string;
}

// Word boundaries keep words like "Monday 5" or "Holiday 3" from matching.
const scheduleTokenPattern = /\b(week|day)\s*(\d+)\b/gi;

/**
 * Pull schedule tokens ("Day 3", "Week 2") out of a search query so users can
 * narrow results to a given week and/or day, optionally combined with text
 * ("Day 5 Lesson"). Tokens may appear anywhere in the query; if a unit is
 * repeated, the last occurrence wins.
 *
 * @param searchField - The raw search query entered by the user
 * @returns The parsed schedule, where a null `week` or `day` means that part
 *   was not specified and `text` holds the remaining query for text search
 *   (empty for a pure schedule lookup), or `null` when the query has no
 *   schedule tokens
 */
const parseWeekDaySearch = (searchField: string): WeekDaySearch | null => {
  const tokens = Array.from(searchField.matchAll(scheduleTokenPattern));

  if (!tokens.length) {
    return null;
  }

  const findTokenValue = (unit: 'week' | 'day'): number | null => {
    const token = tokens.findLast(
      ([, tokenUnit]) => tokenUnit.toLowerCase() === unit
    );
    return token ? Number(token[2]) : null;
  };

  const text = searchField
    .replace(scheduleTokenPattern, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s,]+|[\s,]+$/g, '');

  return { week: findTokenValue('week'), day: findTokenValue('day'), text };
};

export default parseWeekDaySearch;
